import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ResourceHeader from './resource/ResourceHeader';
import BasicInformation from './resource/BasicInformation';
import PermissionRoleSection from './resource/PermissionRoleSection';
import CompensationSection from './resource/CompensationSection';
import AvailabilitySection from './resource/AvailabilitySection';
import { Resource, updateResource, activateResource, deleteResource, inviteResource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import { useAppSystem } from '../hooks/useAppSystem';
import { X, AlertCircle, CheckCircle, Activity, UserCheck, UserX, Users, Plus, MapPin, Navigation, Camera, CalendarDays, Unlink, Loader2, Check, Mail, FileText, ShieldCheck, ChevronRight, Settings } from 'lucide-react';
import ResourceUpgradeModal, { type ResourceUpgradeUsage } from './ResourceUpgradeModal';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { listDocumentSubmissions, type DocumentSubmission } from '../lib/documents';
import SubmissionViewerModal from './documents/SubmissionViewerModal';
import { getPersonInitials } from '../utils/personInitials';

interface ResourceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
  onUpdate: () => void;
}

export default function ResourceDetailsModal({ isOpen, onClose, resource, onUpdate }: ResourceDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const { coreApps, installedAddons } = useAppSystem();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState<{ info?: boolean; pricing?: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Resource>>({});
  const [localResource, setLocalResource] = useState<Resource | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasFutureBookings, setHasFutureBookings] = useState(false);
  const [removingFromBookings, setRemovingFromBookings] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);
  const [showLimitError, setShowLimitError] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [resourceUsage, setResourceUsage] = useState<ResourceUpgradeUsage | null>(null);
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [invitingResource, setInvitingResource] = useState(false);
  const [hasPendingInvite, setHasPendingInvite] = useState(false);

  // Owner-only: offer to make this profile the admin's own team profile —
  // shown only while NO resource in the business is linked to the owner yet.
  const [adminLinkAvailable, setAdminLinkAvailable] = useState(false);
  const [showAdminLinkConfirm, setShowAdminLinkConfirm] = useState(false);
  const [adminLinkLoading, setAdminLinkLoading] = useState(false);

  // Reflect whether a pending invite already exists (so the button says "Resend").
  useEffect(() => {
    let active = true;
    const rid = localResource?.id;
    if (!rid || (localResource as any)?.auth_user_id || !supabase) {
      setHasPendingInvite(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('resource_invites')
        .select('id')
        .eq('resource_id', rid)
        .eq('status', 'pending')
        .limit(1);
      if (active) setHasPendingInvite((data?.length || 0) > 0);
    })();
    return () => { active = false; };
  }, [localResource?.id, (localResource as any)?.auth_user_id]);

  const handleInviteResource = async () => {
    if (!localResource?.id) return;
    setInvitingResource(true);
    try {
      const res = await inviteResource(localResource.id);
      toast.success(res.emailSent ? (hasPendingInvite ? 'Invite resent' : 'Invite email sent') : 'Invite created');
      setHasPendingInvite(true);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send invite');
    } finally {
      setInvitingResource(false);
    }
  };

  // The gear only shows for the business owner while they have no linked
  // team profile anywhere in this business (mirrors the setup-wizard rule).
  useEffect(() => {
    let cancelled = false;
    setShowAdminLinkConfirm(false);
    const ownerId = selectedBusiness?.owner_id;
    if (!isOpen || !localResource?.id || !supabase || !ownerId || sessionUserId !== ownerId) {
      setAdminLinkAvailable(false);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('resources')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .eq('auth_user_id', ownerId)
        .limit(1)
        .maybeSingle();
      if (!cancelled) setAdminLinkAvailable(!data);
    })();
    return () => { cancelled = true; };
  }, [isOpen, localResource?.id, sessionUserId, selectedBusiness?.owner_id, selectedBusiness?.id]);

  /** Link this existing profile to the owner's admin login (replaces any separate resource login). */
  const handleLinkToAdmin = async () => {
    const ownerId = selectedBusiness?.owner_id;
    if (!localResource?.id || !ownerId || !supabase) return;
    setAdminLinkLoading(true);
    try {
      const { data, error: linkErr } = await supabase
        .from('resources')
        .update({ auth_user_id: ownerId, auth_id: ownerId, is_admin: true })
        .eq('id', localResource.id)
        .select()
        .single();
      if (linkErr) throw linkErr;

      // Their old separate login (or pending invite) is no longer needed.
      await supabase
        .from('resource_invites')
        .update({ status: 'revoked' })
        .eq('resource_id', localResource.id)
        .eq('status', 'pending');

      setLocalResource((prev) => (prev ? { ...prev, ...(data as any) } : prev));
      setAdminLinkAvailable(false);
      setShowAdminLinkConfirm(false);
      setHasPendingInvite(false);
      toast.success('Linked to your admin login — this is now your team profile');
      onUpdate();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to link to your admin login');
    } finally {
      setAdminLinkLoading(false);
    }
  };

  // Google Calendar sync state
  const [gcalConnected, setGcalConnected] = useState(false);
  const [gcalEmail, setGcalEmail] = useState<string | null>(null);
  const [gcalSyncId, setGcalSyncId] = useState<string | null>(null);
  const [gcalConnecting, setGcalConnecting] = useState(false);
  const [gcalDisconnecting, setGcalDisconnecting] = useState(false);

  // Check if Payroll app is installed
  const isPayrollAppInstalled = coreApps.some(app => app.app?.slug === 'payroll') || 
                                installedAddons.some(app => app.app?.slug === 'payroll');

  // Check if Docs app is installed (slug 'tasks')
  const isDocsAppInstalled = coreApps.some(app => app.app?.slug === 'tasks') ||
                             installedAddons.some(app => app.app?.slug === 'tasks');

  // Signed documents for this resource
  const [signedDocs, setSignedDocs] = useState<DocumentSubmission[]>([]);
  const [signedDocsLoading, setSignedDocsLoading] = useState(false);
  const [viewSubmissionId, setViewSubmissionId] = useState<string | null>(null);

  useEffect(() => {
    const resourceId = localResource?.id || resource?.id;
    if (!isOpen || !isDocsAppInstalled || !selectedBusiness?.id || !resourceId) { setSignedDocs([]); return; }
    let cancelled = false;
    setSignedDocsLoading(true);
    listDocumentSubmissions(selectedBusiness.id, { resourceId })
      .then((rows) => { if (!cancelled) setSignedDocs(rows); })
      .catch((e) => { console.error('Failed to load signed documents:', e); })
      .finally(() => { if (!cancelled) setSignedDocsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, isDocsAppInstalled, selectedBusiness?.id, localResource?.id, resource?.id]);

  useEffect(() => {
    let cancelled = false;
    supabase?.auth.getUser().then(({ data }) => {
      if (!cancelled) setSessionUserId(data.user?.id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Function to completely reset all state
  const resetAllState = () => {
    console.log("Completely resetting resource modal state");
    setFormData({});
    setLocalResource(null);
    setShowDeleteConfirm(false);
    setEditMode(null);
    setError(null);
    setLoading({});
    setDeactivateLoading(false);
    setActivateLoading(false);
    setDeleteLoading(false);
    setHasFutureBookings(false);
    setRemovingFromBookings(false);
  };

  useEffect(() => {
    if (isOpen && resource) {
      console.log(`Opening resource modal for: ${resource.id} - ${resource.first_name} ${resource.last_name}`);
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      
      // Initialize form data with both commission fields synchronized
      const initialFormData = {
        ...resource,
        // Ensure both commission fields are set if either exists
        commission_percentage: resource.commission_percentage || resource.commission_rate || 0,
        commission_rate: resource.commission_rate || resource.commission_percentage || 0
      };
      
      setFormData(initialFormData);
      setLocalResource(resource);
      setShowDeleteConfirm(false);
    } else {
      console.log("Closing resource modal, cleaning up state");
      document.body.style.overflow = 'unset';
      
      // When modal is being closed, completely reset state
      if (!isOpen) {
        resetAllState();
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, resource]);

  // Realtime: keep location permission data live
  useEffect(() => {
    if (!isOpen || !resource?.id) return;
    const channel = supabase
      .channel(`resource-perm-${resource.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'resources', filter: `id=eq.${resource.id}` },
        (payload: any) => {
          const newPerm = payload.new?.location_permission;
          const newPermAt = payload.new?.location_permission_updated_at;
          if (newPerm !== undefined) {
            setLocalResource(prev => prev ? { ...prev, location_permission: newPerm, location_permission_updated_at: newPermAt } : prev);
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [isOpen, resource?.id]);

  // Load Google Calendar sync status for this resource
  useEffect(() => {
    if (!isOpen || !resource?.id || !selectedBusiness?.id) {
      setGcalConnected(false);
      setGcalEmail(null);
      setGcalSyncId(null);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from('google_calendar_sync')
        .select('id, google_email')
        .eq('business_id', selectedBusiness.id)
        .eq('resource_id', resource.id)
        .eq('is_active', true)
        .maybeSingle();
      if (data) {
        setGcalConnected(true);
        setGcalEmail(data.google_email);
        setGcalSyncId(data.id);
      } else {
        setGcalConnected(false);
        setGcalEmail(null);
        setGcalSyncId(null);
      }
    })();
  }, [isOpen, resource?.id, selectedBusiness?.id]);

  const handleConnectResourceGcal = async () => {
    if (!selectedBusiness?.id || !resource?.id) return;
    setGcalConnecting(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('gcal-oauth-init', {
        body: {
          businessId: selectedBusiness.id,
          resourceId: resource.id,
          returnUrl: `${window.location.origin}/app-store?tab=integrations`,
        },
      });
      if (fnErr) throw fnErr;
      if (!data?.authUrl) throw new Error('No auth URL returned');
      const popup = window.open(data.authUrl, 'gcal-resource-oauth', 'width=600,height=700,left=200,top=100');
      if (!popup) {
        window.location.assign(data.authUrl);
        return;
      }
      const checkPopup = setInterval(async () => {
        if (popup.closed) {
          clearInterval(checkPopup);
          const { data: conn } = await supabase
            .from('google_calendar_sync')
            .select('id, google_email')
            .eq('business_id', selectedBusiness.id!)
            .eq('resource_id', resource!.id)
            .eq('is_active', true)
            .maybeSingle();
          if (conn) { setGcalConnected(true); setGcalEmail(conn.google_email); setGcalSyncId(conn.id); }
          setGcalConnecting(false);
        }
      }, 500);
      setTimeout(() => { clearInterval(checkPopup); if (!popup.closed) popup.close(); setGcalConnecting(false); }, 300000);
    } catch (err) {
      console.error('Error connecting resource Google Calendar:', err);
      toast.error('Could not start the Google Calendar connection. Try again.');
      setGcalConnecting(false);
    }
  };

  const handleDisconnectResourceGcal = async () => {
    if (!gcalSyncId) return;
    setGcalDisconnecting(true);
    try {
      await supabase.from('google_calendar_sync').update({ is_active: false }).eq('id', gcalSyncId);
      setGcalConnected(false);
      setGcalEmail(null);
      setGcalSyncId(null);
    } catch (err) {
      console.error('Error disconnecting resource Google Calendar:', err);
    } finally {
      setGcalDisconnecting(false);
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSave = async (section: string) => {
    if (!resource) return;

    try {
      setLoading({ info: true, pricing: true });
      setError(null);

      // Filter out fields that we've hidden and shouldn't be updated
      const { role, permission_role, ...cleanFormData } = formData;
      
      // Ensure numeric fields are properly converted
      if (cleanFormData.hourly_rate !== undefined) {
        cleanFormData.hourly_rate = Number(cleanFormData.hourly_rate) || 0;
      }
      if (cleanFormData.flat_rate_amount !== undefined) {
        cleanFormData.flat_rate_amount = Number(cleanFormData.flat_rate_amount) || 0;
      }
      
      // Handle commission compensation - ensure both commission_rate and commission_percentage are set and are numbers
      if (cleanFormData.compensation_type === 'commission') {
        const commissionValue = Number(cleanFormData.commission_percentage) || 0;
        cleanFormData.commission_percentage = commissionValue;
        cleanFormData.commission_rate = commissionValue;
      }
      
      // Validate commission percentage
      if (cleanFormData.compensation_type === 'commission' && cleanFormData.commission_percentage !== undefined && 
          (cleanFormData.commission_percentage < 0 || cleanFormData.commission_percentage > 100)) {
        throw new Error('Commission percentage must be between 0 and 100');
      }
      
      // Validate clock in passcode (must be exactly 4 digits if provided)
      if (cleanFormData.clock_in_passcode !== undefined && cleanFormData.clock_in_passcode !== null && cleanFormData.clock_in_passcode !== '') {
        if (!/^\d{4}$/.test(cleanFormData.clock_in_passcode)) {
          throw new Error('Clock in passcode must be exactly 4 digits');
        }
      }
      
      // Ensure required fields are properly set to avoid constraint violations
      if (cleanFormData.is_mobile_available === undefined || cleanFormData.is_mobile_available === null) {
        cleanFormData.is_mobile_available = false;
      }
      
      // Ensure compensation_type is valid
      if (!cleanFormData.compensation_type || !['hourly', 'flat_rate', 'commission'].includes(cleanFormData.compensation_type)) {
        cleanFormData.compensation_type = 'hourly';
      }
      
      console.log('=== RESOURCE SAVE DEBUG ===');
      console.log('Original resource:', JSON.stringify(resource, null, 2));
      console.log('Form data before clean:', JSON.stringify(formData, null, 2));
      console.log('Clean data being sent:', JSON.stringify(cleanFormData, null, 2));
      console.log('Compensation type check:', cleanFormData.compensation_type);
      console.log('Mobile available check:', cleanFormData.is_mobile_available);
      console.log('=== END DEBUG ===');

      const updatedResource = await updateResource(resource.id, cleanFormData);
      
      setLocalResource(updatedResource);
      setEditMode(null);
      
      onUpdate();
    } catch (err) {
      console.error('=== ERROR DETAILS ===');
      console.error('Full error object:', err);
      console.error('Error message:', err instanceof Error ? err.message : 'Unknown error');
      console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      console.error('FormData that failed:', JSON.stringify(formData, null, 2));
      
      // Try to extract more details from the error
      if (err && typeof err === 'object') {
        console.error('Error details:', JSON.stringify(err, null, 2));
        if ('details' in err) {
          console.error('Error details property:', err.details);
        }
        if ('hint' in err) {
          console.error('Error hint:', err.hint);
        }
        if ('code' in err) {
          console.error('Error code:', err.code);
        }
      }
      console.error('=== END ERROR DETAILS ===');
      
      // Provide more specific error messages
      let errorMessage = 'Failed to update team member';
      if (err instanceof Error) {
        console.log('Full error message:', err.message);
        
        if (err.message.includes('permission')) {
          errorMessage = 'Permission denied: You do not have access to update this team member';
        } else if (err.message.includes('check constraint')) {
          if (err.message.includes('compensation_type')) {
            errorMessage = 'Invalid compensation type. Must be hourly, flat_rate, or commission.';
          } else if (err.message.includes('is_mobile_available')) {
            errorMessage = 'Mobile availability setting is required.';
          } else {
            errorMessage = `Database validation error: ${err.message}`;
          }
        } else if (err.message.includes('constraint') || err.message.includes('violates')) {
          errorMessage = `Database constraint violation: ${err.message}`;
        } else if (err.message.includes('network') || err.message.includes('fetch')) {
          errorMessage = 'Network error: Please check your connection and try again';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading({ info: false, pricing: false });
    }
  };

  const handleDeactivate = async () => {
    if (!resource || !supabase) return;

    try {
      setDeactivateLoading(true);
      setError(null);

      // Check for future bookings
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      // First get all booking IDs for this resource
      const { data: bookingResources, error: brError } = await supabase
        .from('booking_resources')
        .select('booking_id')
        .eq('resource_id', resource.id);

      if (brError) {
        console.error('Error checking booking resources:', brError);
        throw new Error('Failed to check for future bookings');
      }

      if (!bookingResources || bookingResources.length === 0) {
        // No bookings assigned, safe to deactivate
      } else {
        // Check if any of these bookings are in the future
        const bookingIds = bookingResources.map(br => br.booking_id);
        
        const { data: futureBookings, error: bookingsError } = await supabase
          .from('bookings')
          .select('id, start_date, status, booking_number')
          .in('id', bookingIds)
          .gte('start_date', todayStr)
          .neq('status', 'cancelled')
          .neq('status', 'completed');

        if (bookingsError) {
          console.error('Error checking future bookings:', bookingsError);
          throw new Error('Failed to check for future bookings');
        }

        if (futureBookings && futureBookings.length > 0) {
          const resourceName = `${resource.first_name} ${resource.last_name}`;
          setError(`Cannot deactivate while ${resourceName} is assigned to ${futureBookings.length} future booking${futureBookings.length !== 1 ? 's' : ''}`);
          setHasFutureBookings(true);
          setDeactivateLoading(false);
          return;
        }
      }

      // No future bookings, proceed with deactivation
      // Also set is_mobile_available to false and delete mobile_availability record
      const updatedResource = await updateResource(resource.id, {
        status: 'inactive',
        is_mobile_available: false
      });

      // Delete the mobile_availability record if it exists
      const { error: mobileAvailabilityError } = await supabase
        .from('mobile_availability')
        .delete()
        .eq('resource_id', resource.id);

      if (mobileAvailabilityError) {
        console.error('Error deleting mobile availability record:', mobileAvailabilityError);
        // Don't fail the entire operation, just log the error
      }
      
      setLocalResource(updatedResource);
      onUpdate();
    } catch (err) {
      console.error('Error deactivating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate team member');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleRemoveFromFutureBookings = async () => {
    if (!resource || !supabase) return;

    try {
      setRemovingFromBookings(true);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      const { data: bookingResources, error: brError } = await supabase
        .from('booking_resources')
        .select('id, booking_id')
        .eq('resource_id', resource.id);

      if (brError) throw new Error('Failed to fetch booking assignments');

      if (bookingResources && bookingResources.length > 0) {
        const bookingIds = bookingResources.map(br => br.booking_id);

        const { data: futureBookings, error: bookingsError } = await supabase
          .from('bookings')
          .select('id')
          .in('id', bookingIds)
          .gte('start_date', todayStr)
          .neq('status', 'cancelled')
          .neq('status', 'completed');

        if (bookingsError) throw new Error('Failed to fetch future bookings');

        if (futureBookings && futureBookings.length > 0) {
          const futureBookingIds = futureBookings.map(b => b.id);

          const { error: deleteError } = await supabase
            .from('booking_resources')
            .delete()
            .eq('resource_id', resource.id)
            .in('booking_id', futureBookingIds);

          if (deleteError) throw new Error('Failed to remove from future bookings');

          toast.success(`Removed from ${futureBookings.length} future booking${futureBookings.length !== 1 ? 's' : ''}`);
        }
      }

      setError(null);
      setHasFutureBookings(false);
      onUpdate();
    } catch (err) {
      console.error('Error removing from future bookings:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove from future bookings');
    } finally {
      setRemovingFromBookings(false);
    }
  };

  const handleActivate = async () => {
    if (!resource || !selectedBusiness?.id) return;

    try {
      setActivateLoading(true);
      setError(null);

      // First check if we can add a resource
      const { data: canAdd, error: checkError } = await supabase
        .rpc('can_add_resource', { p_business_id: selectedBusiness.id });

      if (checkError) {
        console.error('Error checking resource limit:', checkError);
        throw new Error('Failed to check resource limit');
      }

      if (!canAdd) {
        // Get current usage for the error modal
        const { data: usage } = await supabase
          .rpc('get_resource_usage', { p_business_id: selectedBusiness.id });
        
        if (usage && usage.length > 0) {
          const row = usage[0] as {
            active_count: number;
            total_limit: number;
            plan_included_limit?: number;
            purchased_additional_seats?: number;
          };
          const active = row.active_count ?? 0;
          const planIncl = typeof row.plan_included_limit === 'number' ? row.plan_included_limit : 0;
          const purchasedAdditional =
            typeof row.purchased_additional_seats === 'number' ? row.purchased_additional_seats : 0;
          const totalLimit =
            typeof row.total_limit === 'number' && Number.isFinite(row.total_limit)
              ? row.total_limit
              : planIncl + purchasedAdditional;
          const planIncluded = planIncl;
          const isUnlimited = planIncluded === 0;
          setResourceUsage({
            active,
            limit: totalLimit,
            planIncluded,
            purchasedAdditional,
            isUnlimited,
          });
        }
        
        setActivateLoading(false);
        setShowLimitError(true);
        return;
      }

      // If we can add, proceed with activation
      const updatedResource = await updateResource(resource.id, {
        status: 'active'
      });
      
      setLocalResource(updatedResource);
      onUpdate();
      toast.success('Team member activated successfully');
    } catch (err) {
      console.error('Error activating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to activate team member');
    } finally {
      setActivateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!resource) return;
    
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      setError(null);
      
      await deleteResource(resource.id);
      
      onUpdate();
      handleClose();
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete team member');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Add a custom close handler that ensures cleanup
  const handleClose = () => {
    console.log("Resource modal close handler called");
    // Reset state before closing
    resetAllState();
    // Call the provided onClose function
    onClose();
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !supabase || !localResource?.id) return;

    try {
      setIsUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${localResource.id}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('resources')
        .update({ avatar_url: publicUrl })
        .eq('id', localResource.id);

      if (updateError) throw updateError;

      setLocalResource(prev => prev ? { ...prev, avatar_url: publicUrl } : prev);
      toast.success('Profile photo updated');
      onUpdate();
    } catch (err) {
      console.error('Error uploading profile photo:', err);
      toast.error('Failed to upload profile photo');
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!localResource) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      {/* Modal */}
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] lg:w-[800px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Owner-only: link this profile to the admin login (visible only
              while the admin has no linked team profile yet) */}
          {adminLinkAvailable && (
            <button
              onClick={() => setShowAdminLinkConfirm((v) => !v)}
              title="Link to my admin login"
              className="absolute top-4 left-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Settings className="h-5 w-5" />
            </button>
          )}

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Team Member Details
            </h2>
            <div className="flex items-center justify-center gap-2 mt-2">
              <span className="text-sm text-gray-500">
                {localResource.first_name} {localResource.last_name}
              </span>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                localResource.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {localResource.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          {/* Content */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                    {hasFutureBookings && (
                      <button
                        onClick={handleRemoveFromFutureBookings}
                        disabled={removingFromBookings}
                        className="ml-3 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-all duration-150 whitespace-nowrap flex-shrink-0"
                      >
                        {removingFromBookings ? (
                          <div className="flex items-center">
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5"></div>
                            Removing...
                          </div>
                        ) : (
                          'Remove from Future Bookings'
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {showAdminLinkConfirm && adminLinkAvailable && (
                <div className="bg-white border border-blue-200 rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="px-4 py-3 border-b border-blue-200 bg-blue-50">
                    <h3 className="text-base font-semibold text-blue-900 flex items-center">
                      <UserCheck className="h-5 w-5 mr-2" />
                      Make this your team profile
                    </h3>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Link {localResource.first_name} {localResource.last_name} to your admin login. You'll
                      be bookable as this team member and sign in with your existing admin account — no
                      separate team-member login needed.
                      {(localResource as any).auth_user_id
                        ? ' This profile\u2019s current login will be disconnected and can no longer sign in.'
                        : ''}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleLinkToAdmin}
                        disabled={adminLinkLoading}
                        className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {adminLinkLoading ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Linking...
                          </div>
                        ) : (
                          'Link to My Admin Login'
                        )}
                      </button>
                      <button
                        onClick={() => setShowAdminLinkConfirm(false)}
                        disabled={adminLinkLoading}
                        className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 disabled:opacity-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!(localResource as any).auth_user_id && (
                <div className={`rounded-[12px] p-4 flex items-center justify-between gap-3 border ${hasPendingInvite ? 'bg-amber-50 border-amber-200/70' : 'bg-blue-50 border-blue-200/70'}`}>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${hasPendingInvite ? 'text-amber-900' : 'text-blue-900'}`}>{hasPendingInvite ? 'Invite sent' : 'No login yet'}</div>
                    <div className={`text-xs mt-0.5 ${hasPendingInvite ? 'text-amber-700/80' : 'text-blue-700/80'}`}>
                      {hasPendingInvite
                        ? "Waiting for them to accept and set their password. You can resend the invite."
                        : "This team member can be scheduled but can't sign in. Invite them to set up their account."}
                    </div>
                  </div>
                  <button
                    onClick={handleInviteResource}
                    disabled={invitingResource}
                    className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg disabled:opacity-50 transition-colors ${hasPendingInvite ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {invitingResource ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                    {invitingResource ? 'Sending…' : hasPendingInvite ? 'Resend invite' : 'Invite to Nexus'}
                  </button>
                </div>
              )}

              {showDeleteConfirm && (
                <div className="bg-white border border-red-200 rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="px-4 py-3 border-b border-red-200 bg-red-50">
                    <h3 className="text-base font-semibold text-red-800 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2" />
                      Confirm Delete
                    </h3>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-red-700 mb-4">
                      Are you sure you want to permanently delete this team member? This action cannot be undone.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {deleteLoading ? (
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Deleting...
                          </div>
                        ) : (
                          'Confirm Delete'
                        )}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Main Content */}
              <div className="space-y-6">
                {/* Team Member Info Card */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-base font-semibold text-gray-900">Team Member Information</h3>
                  </div>
                  <div className="p-6">
                    {/* Member info layout */}
                    <div className="flex items-start space-x-4">
                      <label className="relative cursor-pointer group flex-shrink-0">
                        <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-sm overflow-hidden">
                          {localResource.avatar_url ? (
                            <img 
                              src={localResource.avatar_url} 
                              alt={`${localResource.first_name} ${localResource.last_name}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            getPersonInitials(localResource.first_name, localResource.last_name)
                          )}
                        </div>
                        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity">
                          <Camera className="h-5 w-5 text-white" />
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          disabled={isUploading}
                        />
                        {isUploading && (
                          <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          </div>
                        )}
                      </label>
                      <div className="flex-1">
                        <div className="text-lg font-semibold text-gray-900">
                          {localResource.first_name} {localResource.last_name}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">{localResource.email}</div>
                        {localResource.phone && (
                          <div className="text-sm text-gray-600">{localResource.phone}</div>
                        )}
                        <div className="flex items-center gap-3 mt-3">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            localResource.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {localResource.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                          
                          {/* Activate/Deactivate Button */}
                          {localResource.status === 'inactive' ? (
                            <button
                              onClick={handleActivate}
                              disabled={activateLoading}
                              className="px-3 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 active:bg-green-800 disabled:opacity-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {activateLoading ? (
                                <div className="flex items-center">
                                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                  Activating...
                                </div>
                              ) : (
                                <>
                                  <UserCheck className="h-3 w-3 mr-2 inline" />
                                  Activate
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={handleDeactivate}
                              disabled={deactivateLoading}
                              className="px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {deactivateLoading ? (
                                <div className="flex items-center">
                                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                  Deactivating...
                                </div>
                              ) : (
                                <>
                                  <UserX className="h-3 w-3 mr-2 inline" />
                                  Deactivate
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        
                        {/* Admin Access Checkbox */}
                        <div className="mt-3 bg-blue-50 border border-blue-200/60 rounded-xl p-4">
                          <label className="flex items-start space-x-3 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={formData.is_admin || false}
                              onChange={async (e) => {
                                const newIsAdmin = e.target.checked;
                                setFormData(prev => ({ ...prev, is_admin: newIsAdmin }));
                                
                                // Save immediately
                                if (!resource) return;
                                try {
                                  await updateResource(resource.id, { is_admin: newIsAdmin });
                                  toast.success(newIsAdmin ? 'Team member promoted to admin' : 'Admin access removed');
                                  onUpdate();
                                } catch (error) {
                                  console.error('Error updating admin status:', error);
                                  toast.error('Failed to update admin status');
                                  // Revert on error
                                  setFormData(prev => ({ ...prev, is_admin: !newIsAdmin }));
                                }
                              }}
                              className="mt-1 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                            />
                            <div className="flex-1">
                              <span className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                Make Admin
                              </span>
                              <p className="text-xs text-gray-600 mt-1">
                                Grant full administrative access to all features and settings
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Permissions Section */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Navigation className="h-4 w-4 text-gray-500" />
                      Location Permissions
                    </h3>
                  </div>
                  <div className="p-6">
                    {(() => {
                      const perm = localResource.location_permission;
                      const updatedAt = localResource.location_permission_updated_at;

                      if (!perm) {
                        return (
                          <div className="flex items-center gap-3 text-gray-500">
                            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                              <MapPin className="h-4 w-4 text-gray-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Not Reported</p>
                              <p className="text-xs text-gray-400">Device hasn't reported location permissions yet</p>
                            </div>
                          </div>
                        );
                      }

                      const isAlways = perm === 'always';
                      const isWhenInUse = perm === 'when_in_use';
                      const isDenied = perm === 'denied' || perm === 'restricted';
                      const isNotDetermined = perm === 'not_determined';

                      const labelMap: Record<string, string> = {
                        always: 'Always Allow',
                        when_in_use: 'While Using App',
                        denied: 'Denied',
                        restricted: 'Restricted',
                        not_determined: 'Not Determined',
                      };

                      const descMap: Record<string, string> = {
                        always: 'Background location is enabled. Full tracking works even when the app is closed.',
                        when_in_use: 'Location only available while app is open. Background tracking will not work.',
                        denied: 'Location access was denied. No location tracking available.',
                        restricted: 'Location is restricted by device policy. No location tracking available.',
                        not_determined: 'User hasn\'t been prompted for location access yet.',
                      };

                      let timeAgo = '';
                      if (updatedAt) {
                        const diff = Date.now() - new Date(updatedAt).getTime();
                        const mins = Math.floor(diff / 60000);
                        if (mins < 1) timeAgo = 'Just now';
                        else if (mins < 60) timeAgo = `${mins}m ago`;
                        else if (mins < 1440) timeAgo = `${Math.floor(mins / 60)}h ago`;
                        else timeAgo = `${Math.floor(mins / 1440)}d ago`;
                      }

                      return (
                        <div className="space-y-3">
                          {/* Main permission status */}
                          <div className="flex items-center gap-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                              isAlways ? 'bg-green-100' : isWhenInUse ? 'bg-yellow-100' : isDenied ? 'bg-red-100' : 'bg-gray-100'
                            }`}>
                              <MapPin className={`h-4 w-4 ${
                                isAlways ? 'text-green-600' : isWhenInUse ? 'text-yellow-600' : isDenied ? 'text-red-600' : 'text-gray-400'
                              }`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-semibold text-gray-900">{labelMap[perm] || perm}</p>
                                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                                  isAlways ? 'bg-green-100 text-green-700' :
                                  isWhenInUse ? 'bg-yellow-100 text-yellow-700' :
                                  isDenied ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-600'
                                }`}>
                                  {isAlways ? 'FULL ACCESS' : isWhenInUse ? 'LIMITED' : isDenied ? 'NO ACCESS' : 'PENDING'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">{descMap[perm] || ''}</p>
                            </div>
                          </div>

                          {/* Details row */}
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Location Services</p>
                              <p className={`text-sm font-semibold mt-1 ${
                                !isDenied && !isNotDetermined ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {!isDenied && !isNotDetermined ? 'Enabled' : 'Disabled'}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3">
                              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Background Location</p>
                              <p className={`text-sm font-semibold mt-1 ${
                                isAlways ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {isAlways ? 'Enabled' : 'Disabled'}
                              </p>
                            </div>
                          </div>

                          {timeAgo && (
                            <p className="text-[10px] text-gray-400 text-right">Last updated: {timeAgo}</p>
                          )}

                          {/* Warning for non-always permission */}
                          {!isAlways && perm !== 'not_determined' && (
                            <div className={`flex items-start gap-2 p-3 rounded-lg ${
                              isDenied ? 'bg-red-50 border border-red-200/60' : 'bg-yellow-50 border border-yellow-200/60'
                            }`}>
                              <AlertCircle className={`h-4 w-4 mt-0.5 shrink-0 ${isDenied ? 'text-red-500' : 'text-yellow-500'}`} />
                              <p className={`text-xs ${isDenied ? 'text-red-700' : 'text-yellow-700'}`}>
                                {isDenied
                                  ? 'This team member has denied location access. They need to enable it in their device Settings > Privacy > Location Services.'
                                  : 'Background location is not enabled. Auto job tracking will only work while the app is open. The team member should set location to "Always" in their device Settings.'
                                }
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Basic Information Section */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <BasicInformation
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />
                </div>

                {/* Permission Role Section */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <PermissionRoleSection
                    resource={localResource}
                    editMode={editMode}
                    onEdit={setEditMode}
                    onUpdate={onUpdate}
                  />
                </div>

                {/* Compensation Section - Only show if Payroll app is installed */}
                {isPayrollAppInstalled && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    <CompensationSection
                      resource={localResource}
                      editMode={editMode}
                      formData={formData}
                      setFormData={setFormData}
                      onSave={handleSave}
                      onEdit={setEditMode}
                      loading={loading.info}
                    />
                  </div>
                )}

                {/* Availability Section */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <AvailabilitySection
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />
                </div>

                {/* Signed Documents Section (Docs app) */}
                {isDocsAppInstalled && (
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900">Signed Documents</h3>
                          <p className="text-xs text-gray-500">Forms completed by this team member</p>
                        </div>
                        {signedDocs.length > 0 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">{signedDocs.length}</span>
                        )}
                      </div>
                      {signedDocsLoading ? (
                        <div className="flex items-center justify-center py-6 text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
                      ) : signedDocs.length === 0 ? (
                        <div className="text-center py-5 text-xs text-gray-400 bg-gray-50 rounded-xl">No signed documents yet.</div>
                      ) : (
                        <div className="space-y-1.5 max-h-72 overflow-y-auto">
                          {signedDocs.map((s) => (
                            <button
                              key={s.id}
                              onClick={() => setViewSubmissionId(s.id)}
                              className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-gray-200/70 hover:border-blue-300 hover:bg-blue-50/40 transition-colors text-left"
                            >
                              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0"><ShieldCheck className="h-4 w-4 text-green-600" /></div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">{s.title || 'Document'}</div>
                                <div className="text-[11px] text-gray-500">{s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''}</div>
                              </div>
                              <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Google Calendar Sync Section */}
                <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <CalendarDays className="h-4 w-4 text-blue-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900">Google Calendar</h3>
                        <p className="text-xs text-gray-500">Sync this resource's bookings with their Google Calendar</p>
                      </div>
                      {gcalConnected && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                          <Check className="h-3 w-3" /> Synced
                        </span>
                      )}
                    </div>
                    {gcalConnected ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                          <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate">{gcalEmail}</span>
                        </div>
                        <button
                          onClick={handleDisconnectResourceGcal}
                          disabled={gcalDisconnecting}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          {gcalDisconnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlink className="h-3.5 w-3.5" />}
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleConnectResourceGcal}
                        disabled={gcalConnecting}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        {gcalConnecting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarDays className="h-3.5 w-3.5" />}
                        Connect Google Calendar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Resource Limit Error Modal */}
      {showLimitError && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[60]"
            style={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
            onClick={() => setShowLimitError(false)} 
          />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
            <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] max-w-sm w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-yellow-100 rounded-full mb-4">
                  <Users className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Team Member Limit Reached
                </h3>
                <p className="text-sm text-gray-600 text-center mb-6">
                  {resourceUsage?.isUnlimited
                    ? 'You cannot add another team member with the current account state.'
                    : `You've reached your limit of ${resourceUsage?.limit ?? 1} team member${(resourceUsage?.limit ?? 1) > 1 ? 's' : ''}. Add a paid seat or upgrade to add more.`}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowLimitError(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:scale-[0.98] transition-all duration-150 text-sm font-semibold touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowLimitError(false);
                      setIsUpgradeModalOpen(true);
                    }}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-[0.98] transition-all duration-150 text-sm font-semibold flex items-center justify-center gap-2 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Another
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Upgrade Modal */}
      <ResourceUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onSuccess={() => {
          setIsUpgradeModalOpen(false);
          onUpdate();
        }}
        businessId={selectedBusiness?.id}
        isBusinessOwner={
          !!(
            selectedBusiness?.owner_id &&
            sessionUserId &&
            selectedBusiness.owner_id === sessionUserId
          )
        }
        currentUsage={resourceUsage || undefined}
      />

      {viewSubmissionId && (
        <SubmissionViewerModal submissionId={viewSubmissionId} onClose={() => setViewSubmissionId(null)} />
      )}
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 